import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { ETabs, FC, Props } from "./typings";

import settingService from "api/settingsService";
import TotpIcon from "assets/icons/add_to_drive.svg?react";
import RemoveIcon from "assets/icons/bin.svg?react";
import EmailIcon from "assets/icons/email.svg?react";
import classNames from "classnames";
import ConfirmModal from "components/ConfirmModal";
import Spinner from "components/Spinner";
import { ITrustedBrowser } from "dto/Settings/ITrustedBrowser";
import { ITwoFactorSettings } from "dto/Settings/ITwoFactorSettings";
import { TwoFactorMethod } from "dto/Settings/TwoFactorMethod";
import moment from "moment";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";
import OtpMethod from "./Methods/OtpMethod";
import WebauthnMethod from "./Methods/WebauthnMethod";
import Button from "components/Button";
import { Tooltip } from "@mui/material";
import Loading from "components/Loading";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import ReactDOM from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import CopyIcon from "assets/icons/copy.svg?react";
import { getKeys } from "utils/getKeys";
import * as openpgp from "openpgp";
import { useTranslation } from "react-i18next";

const SecurityTab: FC<Props> = ({ showTabBar }) => {
  // eslint-disable-line @typescript-eslint/no-unused-vars
  const { t } = useTranslation();
  const [actualView, setActualView] = useState(ETabs.ENC_KEY);
  const {
    data: current2faSettings,
    isSuccess: isCurrent2faSettingsLoaded,
    isLoading,
  } = settingService.useGet2FaSettings();
  const { data: trustedBrowsersData, isSuccess: isTrustedBrowsersLoaded } =
    settingService.useGetTrustedBrowsers();
  const { mutate: removeTrustedBrowser } =
    settingService.useRemoveTrustedBrowser();

  const { mutate: remove2faMethod } = settingService.useRemove2faMethod();

  settingService.useFidoMakeCredentialOptions();
  settingService.useFidoMakeCredential();

  const [browserToDelete, setBrowserToDelete] = useState({
    id: 0,
    show: false,
  });

  const [methodToDelete, setMethodToDelete] = useState({
    method: TwoFactorMethod.None,
    show: false,
  });

  const [method, setMethod] = useState<TwoFactorMethod | null>(null);
  const [enabledMethods, setEnabledMethods] = useState({
    email: false,
    fido: false,
    totp: false,
  });

  const [trustedBrowsers, setTrustedBrowsers] = useState<ITrustedBrowser[]>([]);

  const configure = (method: TwoFactorMethod) => {
    switch (method) {
      case TwoFactorMethod.Email:
        notification({
          title: t("settings.security.configured"),
          text: t("settings.security.emailNoConfig"),
          type: ENotificationType.INFO,
        });
        break;
      default:
        setMethod(method);
        break;
    }
  };

  const handleRemoveBrowser = (browser: ITrustedBrowser) => {
    setBrowserToDelete({ id: browser.id, show: true });
  };

  const handleBrowserDeleteConfirmation = () => {
    removeTrustedBrowser(
      { Id: browserToDelete.id },
      {
        onSuccess() {
          const browsers = trustedBrowsers;
          const newValue = browsers.filter((x) => x.id != browserToDelete.id);
          setTrustedBrowsers(newValue);
          setBrowserToDelete({ id: 0, show: false });
        },
        onError(e: any) {
          setBrowserToDelete({ id: 0, show: false });
          notification({
            title: t("settings.security.removeBrowserFailed"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  const onInnerViewCanceled = () => {
    setMethod(null);
  };

  const onInnerViewSaved = () => {
    switch (method) {
      case TwoFactorMethod.WebAuthn:
        setEnabledMethods({ ...enabledMethods, fido: true });
        break;
      case TwoFactorMethod.Totp:
        setEnabledMethods({ ...enabledMethods, totp: true });
        break;
    }
    setMethod(null);
  };

  const handleRemove2fa = (method: TwoFactorMethod) => {
    setMethodToDelete({ method, show: true });
  };

  const handleRemove2faConfirmed = () => {
    remove2faMethod(
      { method: methodToDelete.method },
      {
        onSuccess() {
          switch (methodToDelete.method) {
            case TwoFactorMethod.WebAuthn:
              setEnabledMethods({ ...enabledMethods, fido: false });
              break;
            case TwoFactorMethod.Totp:
              setEnabledMethods({ ...enabledMethods, totp: false });
              break;
          }
          setMethodToDelete({ method: TwoFactorMethod.None, show: false });
          setMethod(null);
        },
        onError(e: any) {
          notification({
            title: t("settings.security.removeMethodFailed"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (isCurrent2faSettingsLoaded) {
      const s = current2faSettings.data as ITwoFactorSettings;

      setEnabledMethods({
        email: !!s.configurations?.find(
          (x) => x.method === TwoFactorMethod.Email,
        ),
        fido: !!s.configurations?.find(
          (x) => x.method === TwoFactorMethod.WebAuthn,
        ),
        totp: !!s.configurations?.find(
          (x) => x.method === TwoFactorMethod.Totp,
        ),
      });
    }
  }, [current2faSettings]);

  useEffect(() => {
    if (isTrustedBrowsersLoaded) {
      const s = trustedBrowsersData.data as ITrustedBrowser[];
      setTrustedBrowsers(s);
    }
  }, [trustedBrowsersData]);

  if (!current2faSettings) {
    return <Spinner></Spinner>;
  }

  const Menu = () => {
    return (
      <div className={styles.menu}>
        <div
          onClick={() => setActualView(ETabs.ENC_KEY)}
          className={`${styles.box}  ${
            actualView === ETabs.ENC_KEY && styles.activeText
          }`}
        >
          {t("settings.personal.recoveryKey")}
        </div>
        <div
          onClick={() => setActualView(ETabs.FA)}
          className={`${styles.box}  ${
            actualView === ETabs.FA && styles.activeText
          }`}
        >
          {t("settings.security.2faMethods")}
        </div>
        <div
          onClick={() => setActualView(ETabs.TRUST)}
          className={`${styles.box}  ${
            actualView === ETabs.TRUST && styles.activeText
          }`}
        >
          {t("settings.security.trustedDevices")}
        </div>
      </div>
    );
  };

  const Security = () => {
    return (
      <div className={styles.wrapper}>
        <div className={classNames(styles.group, styles.grow)}>
          <div className={styles.header}>{t("settings.security.trustedDevices")}</div>
          <div className={styles.hint}>
            {t("settings.security.trustedDevicesHint")}
          </div>
          <div className={styles.table}>
            <div className={styles.hdr}>
              <div className={styles.date}>{t("common.date")}</div>
              <div className={styles.userAgent}>
                {t("settings.security.browser")}
              </div>
              <div className={styles.ip}>
                {t("settings.security.ipAddress")}
              </div>
            </div>
            <div className={styles.tableContent}>
              {trustedBrowsers.map((browser) => (
                <div className={styles.browser}>
                  <div className={styles.date}>
                    {moment(browser.creationDate)
                      .format("DD.MM.YYYY")
                      .toString()}
                  </div>
                  <div className={styles.userAgent}>{browser.userAgent}</div>
                  <div className={styles.ip}>{browser.ipAddress}</div>

                  <div className={styles.menu}>
                    <Tooltip title={t("common.delete")}>
                      <div>
                        <RemoveIcon
                          className={styles.ic}
                          onClick={() => handleRemoveBrowser(browser)}
                        />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {browserToDelete.show && (
          <ConfirmModal
            title={t("settings.security.confirmRemoveBrowserTitle")}
            text={t("settings.security.confirmRemoveBrowserText")}
            acceptLabel={t("common.delete")}
            cancelLabel={t("common.cancel")}
            onAccept={() => {
              handleBrowserDeleteConfirmation();
            }}
            onCancel={() => setBrowserToDelete({ id: 0, show: false })}
          />
        )}
      </div>
    );
  };

  const EncryptionKey = () => {
    const auth = useAuth();
    const userId = getUserId(auth.user!);

    const [key, setKey] = useState<string>(null);

    const loadKey = async () => {
      const recovKey = localStorage.getItem(`${userId}.recov`);

      if (recovKey?.length) {
        const userKeys = await getKeys(userId);

        const intermediateKey = await openpgp.key.readArmored(
          atob(userKeys!.intermidatePrivateKey!.data),
        );

        const options: openpgp.DecryptOptions = {
          message: await openpgp.message.readArmored(atob(recovKey)),
          privateKeys: intermediateKey.keys,
        };

        openpgp.decrypt(options).then((r) => {
          setKey(r.data);
        });
      }
    };

    useEffect(() => {
      loadKey();
    }, []);

    const qrCodeRef = useRef(null);

    useEffect(() => {
      if (qrCodeRef?.current) {
        const qrData =
          import.meta.env.VITE_APP_SITE + `/recoverqr/${userId}#${key}`;

        ReactDOM.render(
          <QRCodeCanvas value={qrData} size={200} />,
          qrCodeRef?.current,
        );
      }
    }, [qrCodeRef, key]);

    return (
      <div className={styles.wrapper}>
        <div className={classNames(styles.group, styles.grow)}>
          <div className={styles.header}>{t("settings.security.privateKey")}</div>
          <div className={styles.hint}>
            {t("settings.security.privateKeyHint")}
          </div>

          {!!key?.length && (
            <div className={styles.qrCodeWrapper}>
              <div className={styles.qrcode} ref={qrCodeRef}></div>
            </div>
          )}

          {!!key?.length && (
            <div className={styles.keyBoxWrapper}>
              <div className={styles.keyBox}>
                <div className={styles.key}>{key}</div>
                <div
                  className={styles.copy}
                  onClick={() => navigator.clipboard.writeText(key)}
                >
                  <Tooltip title={t("common.copy")}>
                    <div>
                      <CopyIcon></CopyIcon>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (actualView === ETabs.TRUST) {
    return (
      <>
        <Menu />
        <Security />
      </>
    );
  }

  if (actualView === ETabs.ENC_KEY) {
    return (
      <>
        <Menu />
        <EncryptionKey />
      </>
    );
  }

  return (
    <>
      <Menu />
      <div className={styles.wrapper}>
        {method === null && (
          <>
            <div className={styles.group}>
              <div className={styles.header}>
                {t("settings.security.2faHeader")}
              </div>
              <div className={styles.hint}>
                {t("settings.security.2faHint")}
              </div>
              {isLoading && <Loading></Loading>}
              {!isLoading && (
                <div className={styles.methods}>
                  <div
                    className={styles.method}
                    onClick={() => configure(TwoFactorMethod.Email)}
                  >
                    <div className={styles.a}>
                      <div className={styles.icon}>
                        <EmailIcon></EmailIcon>
                      </div>
                      <div className={styles.methodName}>
                        {t("settings.security.emailMethod")}
                        {enabledMethods.email && (
                          <div className={styles.badge}>
                            {t("settings.security.configuredBadge")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      {enabledMethods.email ? (
                        <div className={styles.cta}></div>
                      ) : (
                        <Button onClick={() => {}} text={t("common.add")} />
                      )}
                    </div>
                  </div>
                  {/* <div
                  className={styles.method}
                  onClick={() => configure(TwoFactorMethod.WebAuthn)}
                >
                  <div className={styles.a}>
                    <div className={styles.icon}>
                      <KeyIcon></KeyIcon>
                    </div>
                    <div className={styles.methodName}>
                      Klucz prywatny / Webauthn
                      {enabledMethods.fido && (
                        <div className={styles.badge}>Skonfigurowane</div>
                      )}
                    </div>
                  </div>
                  {enabledMethods.fido ? (
                    <div className={styles.cta}>Edytuj</div>
                  ) : (
                    <Button onClick={() => {}} text="Dodaj" />
                  )}
                </div> */}
                  <div className={styles.method}>
                    <div className={styles.a}>
                      <div className={styles.icon}>
                        <TotpIcon></TotpIcon>
                      </div>
                      <div className={styles.methodName}>
                        Authenticator / OTP
                        {enabledMethods.totp && (
                          <div className={styles.badge}>
                            {t("settings.security.configuredBadge")}
                          </div>
                        )}
                      </div>
                    </div>
                    {enabledMethods.totp ? (
                      <div className={styles.cta}></div>
                    ) : (
                      <Button
                        onClick={() => configure(TwoFactorMethod.Totp)}
                        text={t("common.add")}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {method == TwoFactorMethod.WebAuthn && (
          <WebauthnMethod
            isActive={enabledMethods.fido}
            onCanceled={onInnerViewCanceled}
            onSaved={onInnerViewSaved}
            handleRemove={handleRemove2fa}
          ></WebauthnMethod>
        )}

        {method == TwoFactorMethod.Totp && (
          <OtpMethod
            isActive={enabledMethods.totp}
            onCanceled={onInnerViewCanceled}
            onSaved={onInnerViewSaved}
            handleRemove={handleRemove2fa}
          ></OtpMethod>
        )}

        {methodToDelete.show && (
          <ConfirmModal
            title={t("settings.security.confirmRemoveMethodTitle")}
            text={t("settings.security.confirmRemoveMethodText")}
            acceptLabel={t("common.delete")}
            cancelLabel={t("common.cancel")}
            onAccept={() => {
              handleRemove2faConfirmed();
            }}
            onCancel={() =>
              setMethodToDelete({ method: TwoFactorMethod.None, show: false })
            }
          />
        )}
      </div>
    </>
  );
};

export default SecurityTab;
